import React, { forwardRef, useState } from 'react';
import { X } from 'lucide-react';

/**
 * Modular Input component with prefix/suffix icons, character count, and error states.
 */
const Input = forwardRef(({
  id,
  name,
  label,
  type = 'text',
  value,
  defaultValue,
  onChange,
  placeholder = '',
  disabled = false,
  readOnly = false,
  error,
  helperText,
  mandatory = false,
  prefixIcon,
  suffixIcon,
  clearable = false,
  onClear,
  maxLength,
  showCharCount = false,
  size = 'md', // 'sm' | 'md' | 'lg'
  className = '',
  inputClassName = '',
  ...rest
}, ref) => {
  const [internalVal, setInternalVal] = useState(defaultValue || '');
  const isControlled = value !== undefined;
  const currentVal = isControlled ? value : internalVal;
  const currentLength = String(currentVal || '').length;

  const handleChange = (e) => {
    if (!isControlled) {
      setInternalVal(e.target.value);
    }
    if (onChange) {
      onChange(e);
    }
  };

  const handleClear = () => {
    if (!isControlled) {
      setInternalVal('');
    }
    if (onClear) {
      onClear();
    } else if (onChange) {
      onChange({ target: { id: id || name, name: name || id, value: '' } });
    }
  };

  const sizeClasses = {
    sm: 'h-8 px-2.5 text-xs',
    md: 'h-10 px-3.5 text-sm',
    lg: 'h-12 px-4 text-base',
  };

  const paddingLeftClass = prefixIcon
    ? size === 'sm' ? 'pl-8' : size === 'lg' ? 'pl-11' : 'pl-10'
    : '';

  const paddingRightClass = (clearable && currentVal) || suffixIcon
    ? size === 'sm' ? 'pr-8' : size === 'lg' ? 'pr-11' : 'pr-10'
    : '';

  return (
    <div className={`flex flex-col w-full ${className}`}>
      {/* Label and Character Count Header */}
      {(label || (showCharCount && maxLength)) && (
        <div className="flex items-center justify-between gap-2 mb-1.5">
          {label && (
            <label
              htmlFor={id}
              className="stride-form-label !mb-0"
            >
              {label}
              {mandatory && <span className="text-red-500 ml-0.5">*</span>}
            </label>
          )}

          {showCharCount && maxLength && (
            <span className={`text-xs font-mono ${currentLength >= maxLength ? 'text-red-500 font-bold' : 'text-stride-muted'}`}>
              {currentLength}/{maxLength}
            </span>
          )}
        </div>
      )}

      {/* Input Box with Prefix/Suffix */}
      <div className="relative flex items-center">
        {prefixIcon && (
          <div className="absolute left-3 inset-y-0 flex items-center pointer-events-none text-stride-muted">
            {prefixIcon}
          </div>
        )}

        <input
          ref={ref}
          id={id}
          name={name || id}
          type={type}
          value={currentVal}
          onChange={handleChange}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={readOnly}
          maxLength={maxLength}
          className={`
            w-full rounded-lg bg-white border text-stride-body transition-all duration-150
            focus:outline-none focus:ring-2
            ${error
              ? 'border-red-400 focus:border-red-500 focus:ring-red-100'
              : 'border-border-subtle hover:border-slate-300 focus:border-primary focus:ring-primary/20'
            }
            ${disabled ? 'bg-gray-100 text-stride-muted-light cursor-not-allowed border-border' : ''}
            ${readOnly ? 'bg-gray-50 text-stride-body' : ''}
            ${sizeClasses[size] || sizeClasses.md}
            ${paddingLeftClass}
            ${paddingRightClass}
            ${inputClassName}
          `}
          {...rest}
        />

        {/* Clear Button */}
        {clearable && currentVal && !disabled && !readOnly && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 inset-y-0 flex items-center text-stride-muted hover:text-stride-body focus:outline-none"
            title="Clear text"
          >
            <X size={14} />
          </button>
        )}

        {/* Suffix Icon (if clearable is not shown) */}
        {suffixIcon && (!clearable || !currentVal) && (
          <div className="absolute right-3 inset-y-0 flex items-center pointer-events-none text-stride-muted">
            {suffixIcon}
          </div>
        )}
      </div>

      {/* Error or Helper Message */}
      {error && <span className="stride-form-error">{error}</span>}
      {helperText && !error && (
        <span className="stride-form-helper">{helperText}</span>
      )}
    </div>
  );
});

Input.displayName = 'Input';

/**
 * Modular Textarea component matching the exact Input style tokens.
 */
export const Textarea = forwardRef(({
  id,
  name,
  label,
  value,
  defaultValue,
  onChange,
  placeholder = '',
  disabled = false,
  readOnly = false,
  error,
  helperText,
  mandatory = false,
  maxLength,
  showCharCount = false,
  rows = 4,
  className = '',
  inputClassName = '',
  ...rest
}, ref) => {
  const [internalVal, setInternalVal] = useState(defaultValue || '');
  const isControlled = value !== undefined;
  const currentVal = isControlled ? value : internalVal;
  const currentLength = String(currentVal || '').length;

  const handleChange = (e) => {
    if (!isControlled) {
      setInternalVal(e.target.value);
    }
    if (onChange) {
      onChange(e);
    }
  };

  return (
    <div className={`flex flex-col w-full ${className}`}>
      {(label || (showCharCount && maxLength)) && (
        <div className="flex items-center justify-between gap-2 mb-1.5">
          {label && (
            <label
              htmlFor={id}
              className="stride-form-label !mb-0"
            >
              {label}
              {mandatory && <span className="text-red-500 ml-0.5">*</span>}
            </label>
          )}

          {showCharCount && maxLength && (
            <span className={`text-xs font-mono ${currentLength >= maxLength ? 'text-red-500 font-bold' : 'text-stride-muted'}`}>
              {currentLength}/{maxLength}
            </span>
          )}
        </div>
      )}

      <textarea
        ref={ref}
        id={id}
        name={name || id}
        value={currentVal}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={readOnly}
        maxLength={maxLength}
        rows={rows}
        className={`
          w-full rounded-lg bg-white border text-stride-body transition-all duration-150 p-3 text-sm
          focus:outline-none focus:ring-2 resize-y leading-relaxed
          ${error
            ? 'border-red-400 focus:border-red-500 focus:ring-red-100'
            : 'border-border-subtle hover:border-slate-300 focus:border-primary focus:ring-primary/20'
          }
          ${disabled ? 'bg-slate-50 text-stride-muted-light cursor-not-allowed border-border' : ''}
          ${readOnly ? 'bg-slate-50 text-stride-body' : ''}
          ${inputClassName}
        `}
        {...rest}
      />

      {error && <span className="stride-form-error">{error}</span>}
      {helperText && !error && (
        <span className="stride-form-helper">{helperText}</span>
      )}
    </div>
  );
});

Textarea.displayName = 'Textarea';

export default Input;
export { Input };

